// UC 34259 — Log Raw Material Stock Receipt
// UC 34272 — Perform Manual Stock Adjustment
// UC 34263 — Monitor Live Stock Ledger and Alerts
// UC 34265 — Raise Low-Stock Reorder Request
// Role: Procurement Staff / Commissary Supervisor
// Mirrors RawHTMLDesign/raw-material-inventory/raw-material-receipt-&-stock-ledger-page.html

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../../services/api.js';

function tomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

const PANEL_RECEIPT = 'receipt';
const PANEL_ADJUST = 'adjust';
const PANEL_ALERTS = 'alerts';
const PANEL_REORDER = 'reorder';

const SEVERITY_BADGE = {
  expired: 'badge-danger',
  negative: 'badge-danger',
  low_stock: 'badge-warning',
  expiring_soon: 'badge-warning',
};

const SEVERITY_LABEL = {
  expired: 'Expired Batch',
  negative: 'Negative Balance',
  low_stock: 'Low Stock',
  expiring_soon: 'Expiring Soon',
};

export default function RawMaterialReceiptAndStockLedgerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [overview, setOverview] = useState([]);
  const [alertsList, setAlertsList] = useState([]);
  const [filterItemId, setFilterItemId] = useState('');
  const [activePanel, setActivePanel] = useState(PANEL_RECEIPT);

  // ── Ledger filter state (URL-synced) ────────────────────────────────────
  const searchQ = searchParams.get('q') || '';
  const selectedCategories = useMemo(
    () => (searchParams.get('category') || '').split(',').filter(Boolean),
    [searchParams],
  );
  const lowStockActive = searchParams.get('low_stock') === 'true';
  const nearExpiryActive = searchParams.get('near_expiry') === 'true';
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(searchQ);
  const searchTimer = useRef(null);

  // Category map: item.id → category
  const categoryMap = useMemo(
    () => Object.fromEntries(items.map((i) => [i.id, i.category || ''])),
    [items],
  );
  const allCategories = useMemo(
    () => [...new Set(overview.map((r) => categoryMap[r.id] || '').filter(Boolean))].sort(),
    [overview, categoryMap],
  );

  // ── Status helpers ───────────────────────────────────────────────────────
  const NEAR_EXPIRY_DAYS = 7;
  function rowIsLowStock(row) { return !!row.is_low_stock; }
  function rowIsNearExpiry(row) { return (row.alert_conditions || []).some((c) => c === 'expiring_soon'); }
  function rowStatus(row) {
    const low = rowIsLowStock(row);
    const exp = rowIsNearExpiry(row);
    if (low && exp) return 'Critical';
    if (low) return 'Low Stock';
    if (exp) return 'Near Expiry';
    return 'OK';
  }

  const lowStockCount = useMemo(() => overview.filter(rowIsLowStock).length, [overview]);
  const nearExpiryCount = useMemo(() => overview.filter(rowIsNearExpiry).length, [overview]);

  // ── Filtered ledger ──────────────────────────────────────────────────────
  const filteredOverview = useMemo(() => overview.filter((row) => {
    const q = searchQ.toLowerCase();
    if (q && !row.name.toLowerCase().includes(q) && !row.item_code.toLowerCase().startsWith(q)) return false;
    if (selectedCategories.length && !selectedCategories.includes(categoryMap[row.id] || '')) return false;
    if (lowStockActive && !rowIsLowStock(row)) return false;
    if (nearExpiryActive && !rowIsNearExpiry(row)) return false;
    return true;
  }), [overview, searchQ, selectedCategories, lowStockActive, nearExpiryActive, categoryMap]);

  // ── URL helpers ───────────────────────────────────────────────────────────
  function updateParam(key, value) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (!value || (Array.isArray(value) && !value.length)) p.delete(key);
      else p.set(key, Array.isArray(value) ? value.join(',') : value);
      return p;
    }, { replace: true });
  }
  function clearAllFilters() {
    setSearchInput('');
    clearTimeout(searchTimer.current);
    setSearchParams({}, { replace: true });
  }
  function handleSearchInput(val) {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => updateParam('q', val), 200);
  }
  function toggleCategory(cat) {
    const next = selectedCategories.includes(cat)
      ? selectedCategories.filter((c) => c !== cat)
      : [...selectedCategories, cat];
    updateParam('category', next);
  }
  function toggleLowStock() { updateParam('low_stock', lowStockActive ? '' : 'true'); }
  function toggleNearExpiry() { updateParam('near_expiry', nearExpiryActive ? '' : 'true'); }

  // ── CSV Export ────────────────────────────────────────────────────────────
  function exportLedgerCSV() {
    const today = new Date().toISOString().slice(0, 10);
    const headers = ['SKU Code','Ingredient Name','Category','Unit of Measure','On Hand Qty','Par Level','Status','Batch Number','Expiry Date','Storage Location','Last Updated'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const dataRows = filteredOverview.map((row) => [
      row.item_code, row.name, categoryMap[row.id] || '', row.uom,
      Number(row.total_balance).toFixed(3),
      row.safety_stock_threshold != null ? Number(row.safety_stock_threshold).toFixed(3) : '',
      rowStatus(row), '', '', '',
      row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '',
    ]);
    const csv = [headers, ...dataRows].map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `stock-ledger-${today}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // Drill-down batches
  const [drillItem, setDrillItem] = useState(null); // {id, name}
  const [batches, setBatches] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);

  // Receipt form
  const [rcptForm, setRcptForm] = useState({
    item_id: '', vendor_name: '', batch_lot_number: '', quantity: '', unit: '',
    expiry_date: tomorrow(), purchase_cost: '', notes: '',
  });
  const [rcptSubmitting, setRcptSubmitting] = useState(false);
  const [rcptError, setRcptError] = useState('');

  // Adjustment form
  const [adjForm, setAdjForm] = useState({ item_id: '', adjustment_qty: '', unit: '', reason: '' });
  const [adjSubmitting, setAdjSubmitting] = useState(false);
  const [adjError, setAdjError] = useState('');
  const [adjNegWarning, setAdjNegWarning] = useState(null);

  const [banner, setBanner] = useState({ message: '', kind: '' });

  // Reorder form (UC 34265)
  const [suppliers, setSuppliers] = useState([]);
  const [reorderList, setReorderList] = useState([]);
  const [reorderForm, setReorderForm] = useState({
    item_id: '', supplier_id: '', requested_qty: '', required_by_date: '', urgency: 'Medium', notes: '',
  });
  const [reorderSubmitting, setReorderSubmitting] = useState(false);
  const [reorderError, setReorderError] = useState('');

  const refresh = useCallback(async () => {
    const [iRes, rRes, aRes, ovRes, alRes, supRes, rrRes] = await Promise.all([
      api.get('/master-data/items').then((x) => (x.data.data || []).filter((i) => i.is_active !== false)).catch(() => []),
      api.get('/stock-receipts').then((x) => x.data.data || []).catch(() => []),
      api.get('/stock-adjustments').then((x) => x.data.data || []).catch(() => []),
      api.get('/stock-ledger/overview').then((x) => x.data.data || []).catch(() => []),
      api.get('/stock-ledger/alerts').then((x) => x.data.data || []).catch(() => []),
      api.get('/master-data/suppliers').then((x) => x.data.data || []).catch(() => []),
      api.get('/reorder-requests').then((x) => x.data.data || []).catch(() => []),
    ]);
    setItems(iRes);
    setReceipts(rRes);
    setAdjustments(aRes);
    setOverview(ovRes);
    setAlertsList(alRes);
    setSuppliers(supRes);
    setReorderList(rrRes);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function setRcptField(n, v) { setRcptForm((f) => ({ ...f, [n]: v })); }
  function setAdjField(n, v) { setAdjForm((f) => ({ ...f, [n]: v })); }

  function handleRcptItemChange(id) {
    setRcptField('item_id', id);
    const item = items.find((i) => i.id === id);
    if (item) setRcptField('unit', item.uom || '');
  }
  function handleAdjItemChange(id) {
    setAdjField('item_id', id);
    const item = items.find((i) => i.id === id);
    if (item) setAdjField('unit', item.uom || '');
  }

  const filteredReceipts = filterItemId ? receipts.filter((r) => r.item_id === filterItemId) : receipts;
  const filteredAdjustments = filterItemId ? adjustments.filter((a) => a.item_id === filterItemId) : adjustments;

  async function openDrillDown(item) {
    setDrillItem(item);
    setBatchLoading(true);
    try {
      const res = await api.get(`/stock-ledger/batches/${item.id}`);
      setBatches(res.data.data || []);
    } catch { setBatches([]); }
    finally { setBatchLoading(false); }
  }

  async function quarantineBatch(batchId) {
    try {
      await api.post(`/stock-ledger/batches/${batchId}/quarantine`);
      setBanner({ message: 'Batch quarantined. Stock deducted from active totals.', kind: 'success' });
      // Refresh batches
      if (drillItem) {
        const res = await api.get(`/stock-ledger/batches/${drillItem.id}`);
        setBatches(res.data.data || []);
      }
      await refresh();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Quarantine failed.', kind: 'error' });
    }
  }

  // ── Receipt submit ────────────────────────────────────────────────────────
  async function submitReceipt() {
    setRcptError(''); setBanner({ message: '', kind: '' });
    if (!rcptForm.item_id) { setRcptError('Please select an ingredient/SKU.'); return; }
    if (!rcptForm.quantity || Number(rcptForm.quantity) <= 0) { setRcptError('Quantity must be greater than zero.'); return; }
    if (!rcptForm.expiry_date) { setRcptError('Expiry date is required for FEFO tracking.'); return; }
    setRcptSubmitting(true);
    try {
      const res = await api.post('/stock-receipts', {
        item_id: rcptForm.item_id,
        vendor_name: rcptForm.vendor_name || null,
        batch_lot_number: rcptForm.batch_lot_number || null,
        quantity: Number(rcptForm.quantity),
        unit: rcptForm.unit || null,
        expiry_date: rcptForm.expiry_date,
        purchase_cost: rcptForm.purchase_cost ? Number(rcptForm.purchase_cost) : null,
        notes: rcptForm.notes || null,
      });
      const { item_name, new_balance, unit } = res.data;
      setBanner({ message: `Stock receipt logged. New balance for ${item_name}: ${Number(new_balance).toFixed(3)} ${unit}.`, kind: 'success' });
      setRcptForm({ item_id: '', vendor_name: '', batch_lot_number: '', quantity: '', unit: '', expiry_date: tomorrow(), purchase_cost: '', notes: '' });
      await refresh();
    } catch (err) {
      setRcptError(err?.response?.data?.error || 'Failed to log receipt.');
    } finally { setRcptSubmitting(false); }
  }

  // ── Adjustment submit ─────────────────────────────────────────────────────
  async function submitAdjustment(forceConfirm = false) {
    setAdjError(''); setBanner({ message: '', kind: '' });
    if (!adjForm.item_id) { setAdjError('Please select an ingredient/SKU.'); return; }
    if (!adjForm.adjustment_qty || Number(adjForm.adjustment_qty) === 0) { setAdjError('Adjustment quantity must be a non-zero number.'); return; }
    if (!adjForm.reason.trim()) { setAdjError('A reason note is required for audit traceability.'); return; }
    setAdjSubmitting(true);
    try {
      const res = await api.post('/stock-adjustments', {
        item_id: adjForm.item_id,
        adjustment_qty: Number(adjForm.adjustment_qty),
        unit: adjForm.unit || null,
        reason: adjForm.reason,
        confirm_negative: forceConfirm,
      });
      if (res.status === 202 && res.data.requires_confirmation) {
        setAdjNegWarning(res.data); setAdjSubmitting(false); return;
      }
      const { item_name, balance_after, went_negative } = res.data;
      setBanner({ message: `Adjustment saved. ${item_name} new balance: ${Number(balance_after).toFixed(3)} ${adjForm.unit}.${went_negative ? ' Warning: stock is now negative.' : ''}`, kind: went_negative ? 'warning' : 'success' });
      setAdjForm({ item_id: '', adjustment_qty: '', unit: '', reason: '' });
      setAdjNegWarning(null);
      await refresh();
    } catch (err) {
      setAdjError(err?.response?.data?.error || 'Failed to save adjustment.');
    } finally { setAdjSubmitting(false); }
  }

  async function confirmNegativeAdjustment() {
    setAdjNegWarning(null);
    await submitAdjustment(true);
  }

  // ── Reorder helpers (UC 34265) ────────────────────────────────────────────
  function setReorderField(n, v) { setReorderForm((f) => ({ ...f, [n]: v })); }

  function prefillReorder(alertItem) {
    // Pre-fill from an alert/overview row
    setReorderForm((f) => ({
      ...f,
      item_id: alertItem.id,
      requested_qty: alertItem.safety_stock_threshold
        ? String(Math.max(0, Number(alertItem.safety_stock_threshold) - Number(alertItem.total_balance)))
        : '',
      urgency: 'High',
      notes: `Low stock alert — current balance: ${Number(alertItem.total_balance).toFixed(3)} ${alertItem.uom}`,
    }));
    setActivePanel(PANEL_REORDER);
  }

  async function submitReorder() {
    setReorderError(''); setBanner({ message: '', kind: '' });
    if (!reorderForm.item_id) { setReorderError('Please select an ingredient/SKU.'); return; }
    if (!reorderForm.requested_qty || Number(reorderForm.requested_qty) <= 0) {
      setReorderError('Requested quantity must be greater than zero.'); return;
    }
    if (!reorderForm.required_by_date) { setReorderError('Required-by date is required.'); return; }
    setReorderSubmitting(true);
    try {
      const res = await api.post('/reorder-requests', {
        item_id: reorderForm.item_id,
        supplier_id: reorderForm.supplier_id || null,
        requested_qty: Number(reorderForm.requested_qty),
        required_by_date: reorderForm.required_by_date,
        urgency: reorderForm.urgency,
        notes: reorderForm.notes || null,
      });
      const { requisition_ref, item_name } = res.data.data;
      setBanner({ message: `Reorder request ${requisition_ref} raised for ${item_name}.`, kind: 'success' });
      setReorderForm({ item_id: '', supplier_id: '', requested_qty: '', required_by_date: '', urgency: 'Medium', notes: '' });
      await refresh();
    } catch (err) {
      setReorderError(err?.response?.data?.error || 'Failed to raise reorder request.');
    } finally { setReorderSubmitting(false); }
  }

  return (
    <div className="container-fluid px-4 py-4" data-testid="stock-receipt-page">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h1 className="h3 font-weight-bold text-dark mb-1">
              <i className="fa-solid fa-boxes-stacked mr-2 text-primary" />
              Raw Material Receipt &amp; Stock Ledger
            </h1>
            <p className="text-muted mb-0">Log stock receipts, manual adjustments, and monitor live inventory alerts.</p>
          </div>
          <span className="badge badge-light border px-3 py-2 text-dark shadow-sm mt-2 mt-sm-0">
            <i className="fa-solid fa-user-shield text-teal mr-1" />Role: <strong>Procurement Staff / Commissary Supervisor</strong>
          </span>
        </div>
      </div>

      {/* Banner */}
      {banner.message && (
        <div
          className={`alert mb-3 ${banner.kind === 'error' ? 'alert-danger' : banner.kind === 'warning' ? 'alert-warning' : 'alert-success'}`}
          data-testid={banner.kind === 'error' ? 'receipt-error-banner' : 'receipt-success-banner'}
        >
          {banner.message}
        </div>
      )}

      <div className="row">
        {/* Left panel with tabs */}
        <div className="col-xl-4 col-lg-5 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white py-2 border-bottom">
              <ul className="nav nav-pills card-header-pills">
                <li className="nav-item">
                  <button className={`nav-link btn btn-link ${activePanel === PANEL_RECEIPT ? 'active' : ''}`} onClick={() => { setActivePanel(PANEL_RECEIPT); setRcptError(''); setAdjError(''); }} data-testid="tab-receipt">
                    <i className="fa-solid fa-circle-plus mr-1" />Log Receipt
                  </button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link btn btn-link ${activePanel === PANEL_ADJUST ? 'active' : ''}`} onClick={() => { setActivePanel(PANEL_ADJUST); setRcptError(''); setAdjError(''); }} data-testid="tab-adjust">
                    <i className="fa-solid fa-pen-to-square mr-1" />Adjust
                  </button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link btn btn-link ${activePanel === PANEL_ALERTS ? 'active' : ''}`} onClick={() => setActivePanel(PANEL_ALERTS)} data-testid="tab-alerts">
                    <i className="fa-solid fa-bell mr-1" />
                    Alerts
                    {alertsList.length > 0 && <span className="badge badge-danger ml-1">{alertsList.length}</span>}
                  </button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link btn btn-link ${activePanel === PANEL_REORDER ? 'active' : ''}`} onClick={() => { setActivePanel(PANEL_REORDER); setReorderError(''); }} data-testid="tab-reorder">
                    <i className="fa-solid fa-cart-plus mr-1" />Reorder
                  </button>
                </li>
              </ul>
            </div>

            {/* ── Receipt Form ── */}
            {activePanel === PANEL_RECEIPT && (
              <div className="card-body" data-testid="receipt-form-panel">
                {rcptError && <div className="alert alert-danger py-2 mb-3" data-testid="receipt-form-error">{rcptError}</div>}
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Ingredient / SKU <span className="text-danger">*</span></label>
                  <select className="form-control custom-select" value={rcptForm.item_id} onChange={(e) => handleRcptItemChange(e.target.value)} data-testid="receipt-item">
                    <option value="" disabled>Select Ingredient...</option>
                    {items.map((i) => <option key={i.id} value={i.id}>{i.item_code} — {i.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Vendor Name</label>
                  <input type="text" className="form-control" placeholder="e.g. Global Foods Supplier" value={rcptForm.vendor_name} onChange={(e) => setRcptField('vendor_name', e.target.value)} data-testid="receipt-vendor" />
                </div>
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Batch / Lot Number</label>
                  <input type="text" className="form-control" placeholder="e.g. BATCH-2026-001" value={rcptForm.batch_lot_number} onChange={(e) => setRcptField('batch_lot_number', e.target.value)} data-testid="receipt-batch" />
                </div>
                <div className="form-row">
                  <div className="form-group col-7">
                    <label className="font-weight-600 text-dark">Received Qty <span className="text-danger">*</span></label>
                    <input type="number" className="form-control" min="0.001" step="0.001" placeholder="0.000" value={rcptForm.quantity} onChange={(e) => setRcptField('quantity', e.target.value)} data-testid="receipt-qty" />
                  </div>
                  <div className="form-group col-5">
                    <label className="font-weight-600 text-dark">Unit</label>
                    <input type="text" className="form-control" placeholder="kg" value={rcptForm.unit} onChange={(e) => setRcptField('unit', e.target.value)} data-testid="receipt-unit" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Expiry Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control" value={rcptForm.expiry_date} onChange={(e) => setRcptField('expiry_date', e.target.value)} data-testid="receipt-expiry" />
                  <small className="text-muted">Required for FEFO inventory tracking.</small>
                </div>
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Purchase Cost (per unit)</label>
                  <div className="input-group">
                    <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                    <input type="number" className="form-control" min="0" step="0.01" placeholder="0.00" value={rcptForm.purchase_cost} onChange={(e) => setRcptField('purchase_cost', e.target.value)} data-testid="receipt-cost" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Notes</label>
                  <textarea className="form-control" rows="2" placeholder="Optional notes..." value={rcptForm.notes} onChange={(e) => setRcptField('notes', e.target.value)} data-testid="receipt-notes" />
                </div>
                <button type="button" className="btn btn-primary btn-block mt-2" onClick={submitReceipt} disabled={rcptSubmitting} data-testid="btn-log-receipt">
                  <i className="fa-solid fa-save mr-1" />{rcptSubmitting ? 'Logging...' : 'Log Stock Receipt'}
                </button>
              </div>
            )}

            {/* ── Adjustment Form ── */}
            {activePanel === PANEL_ADJUST && (
              <div className="card-body" data-testid="adjust-form-panel">
                {adjError && <div className="alert alert-danger py-2 mb-3" data-testid="adjust-form-error">{adjError}</div>}
                {adjNegWarning && (
                  <div className="alert alert-warning mb-3" data-testid="adjust-neg-warning">
                    <strong>Warning:</strong> {adjNegWarning.message}
                    <div className="mt-1 small">Before: <strong>{Number(adjNegWarning.balance_before).toFixed(3)}</strong> &rarr; After: <strong className="text-danger">{Number(adjNegWarning.balance_after).toFixed(3)}</strong></div>
                    <button type="button" className="btn btn-warning btn-sm btn-block mt-2" onClick={confirmNegativeAdjustment} disabled={adjSubmitting} data-testid="btn-confirm-adj-negative">
                      Confirm Negative Adjustment
                    </button>
                  </div>
                )}
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Ingredient / SKU <span className="text-danger">*</span></label>
                  <select className="form-control custom-select" value={adjForm.item_id} onChange={(e) => handleAdjItemChange(e.target.value)} data-testid="adj-item">
                    <option value="" disabled>Select Ingredient...</option>
                    {items.map((i) => <option key={i.id} value={i.id}>{i.item_code} — {i.name}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group col-7">
                    <label className="font-weight-600 text-dark">Adjustment Qty <span className="text-danger">*</span></label>
                    <input type="number" className="form-control" step="0.001" placeholder="+10.000 or -5.000" value={adjForm.adjustment_qty} onChange={(e) => setAdjField('adjustment_qty', e.target.value)} data-testid="adj-qty" />
                    <small className="text-muted">Use negative values for deductions.</small>
                  </div>
                  <div className="form-group col-5">
                    <label className="font-weight-600 text-dark">Unit</label>
                    <input type="text" className="form-control" placeholder="kg" value={adjForm.unit} onChange={(e) => setAdjField('unit', e.target.value)} data-testid="adj-unit" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Reason <span className="text-danger">*</span></label>
                  <textarea className="form-control" rows="3" placeholder="Reason for adjustment (e.g. Breakage, Spillage, Stock count correction)" value={adjForm.reason} onChange={(e) => setAdjField('reason', e.target.value)} data-testid="adj-reason" />
                  <small className="text-muted">Required for audit traceability.</small>
                </div>
                <button type="button" className="btn btn-warning btn-block mt-2" onClick={() => submitAdjustment()} disabled={adjSubmitting} data-testid="btn-save-adjustment">
                  <i className="fa-solid fa-pen-to-square mr-1" />{adjSubmitting ? 'Saving...' : 'Save Adjustment'}
                </button>
              </div>
            )}

            {/* ── Alerts Panel ── */}
            {activePanel === PANEL_ALERTS && (
              <div className="card-body p-0" data-testid="alerts-panel">
                {alertsList.length === 0 ? (
                  <div className="p-4 text-center text-muted" data-testid="alerts-empty">No active inventory alerts.</div>
                ) : (
                  <ul className="list-group list-group-flush">
                    {alertsList.map((it) => (
                      <li key={it.id} className="list-group-item py-3" data-testid={`alert-item-${it.id}`}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong data-testid={`alert-name-${it.id}`}>{it.name}</strong>
                            <br />
                            <small className="text-muted">{it.item_code} — Balance: {Number(it.total_balance).toFixed(3)} {it.uom}</small>
                          </div>
                          <span className={`badge ${SEVERITY_BADGE[it.alert_severity] || 'badge-secondary'} ml-2`} data-testid={`alert-badge-${it.id}`}>
                            {SEVERITY_LABEL[it.alert_severity] || 'Alert'}
                          </span>
                        </div>
                        {(it.alert_conditions || []).length > 1 && (
                          <small className="text-muted d-block mt-1">
                            Also: {it.alert_conditions.slice(1).join(', ')}
                          </small>
                        )}
                        {!it.threshold_configured && (
                          <small className="text-warning d-block mt-1" data-testid={`alert-no-threshold-${it.id}`}>
                            Threshold not configured
                          </small>
                        )}
                        <div className="d-flex gap-2 mt-2" style={{ gap: '6px' }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => { openDrillDown(it); setActivePanel(PANEL_ALERTS); }}
                            data-testid={`btn-drilldown-${it.id}`}
                          >
                            View Batches
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary ml-1"
                            onClick={() => prefillReorder(it)}
                            data-testid={`btn-raise-reorder-${it.id}`}
                          >
                            <i className="fa-solid fa-cart-plus mr-1" />Raise Reorder
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* ── Reorder Form (UC 34265) ── */}
            {activePanel === PANEL_REORDER && (
              <div className="card-body" data-testid="reorder-form-panel">
                {reorderError && <div className="alert alert-danger py-2 mb-3" data-testid="reorder-form-error">{reorderError}</div>}
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Ingredient / SKU <span className="text-danger">*</span></label>
                  <select className="form-control custom-select" value={reorderForm.item_id} onChange={(e) => setReorderField('item_id', e.target.value)} data-testid="reorder-item">
                    <option value="" disabled>Select Ingredient...</option>
                    {items.map((i) => <option key={i.id} value={i.id}>{i.item_code} — {i.name}</option>)}
                  </select>
                  {reorderForm.item_id && (() => {
                    const ov = overview.find((o) => o.id === reorderForm.item_id);
                    if (!ov) return null;
                    return (
                      <small className="text-muted d-block mt-1" data-testid="reorder-stock-context">
                        Current stock: <strong>{Number(ov.total_balance).toFixed(3)} {ov.uom}</strong>
                        {ov.safety_stock_threshold != null && ` | Safety threshold: ${Number(ov.safety_stock_threshold).toFixed(1)} ${ov.safety_stock_unit || ov.uom}`}
                      </small>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Preferred Supplier</label>
                  <select className="form-control custom-select" value={reorderForm.supplier_id} onChange={(e) => setReorderField('supplier_id', e.target.value)} data-testid="reorder-supplier">
                    <option value="">None / To be determined</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.supplier_code})</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group col-7">
                    <label className="font-weight-600 text-dark">Requested Qty <span className="text-danger">*</span></label>
                    <input type="number" className="form-control" min="0.001" step="0.001" placeholder="0.000" value={reorderForm.requested_qty} onChange={(e) => setReorderField('requested_qty', e.target.value)} data-testid="reorder-qty" />
                  </div>
                  <div className="form-group col-5">
                    <label className="font-weight-600 text-dark">Urgency</label>
                    <select className="form-control custom-select" value={reorderForm.urgency} onChange={(e) => setReorderField('urgency', e.target.value)} data-testid="reorder-urgency">
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Required By <span className="text-danger">*</span></label>
                  <input type="date" className="form-control" value={reorderForm.required_by_date} onChange={(e) => setReorderField('required_by_date', e.target.value)} data-testid="reorder-date" />
                </div>
                <div className="form-group">
                  <label className="font-weight-600 text-dark">Notes</label>
                  <textarea className="form-control" rows="3" placeholder="Additional context for this reorder..." value={reorderForm.notes} onChange={(e) => setReorderField('notes', e.target.value)} data-testid="reorder-notes" />
                </div>
                <button type="button" className="btn btn-primary btn-block mt-2" onClick={submitReorder} disabled={reorderSubmitting} data-testid="btn-submit-reorder">
                  <i className="fa-solid fa-paper-plane mr-1" />{reorderSubmitting ? 'Submitting...' : 'Raise Reorder Request'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: Main Content */}
        <div className="col-xl-8 col-lg-7 mb-4">
          {/* Filter (for receipt/adjustment view) */}
          {activePanel !== PANEL_ALERTS && activePanel !== PANEL_REORDER && (
            <div className="mb-3 d-flex justify-content-end">
              <select className="form-control form-control-sm custom-select" style={{ maxWidth: 240 }} value={filterItemId} onChange={(e) => setFilterItemId(e.target.value)} data-testid="filter-ledger-item">
                <option value="">All Ingredients</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.item_code} — {i.name}</option>)}
              </select>
            </div>
          )}

          {/* Inventory Overview / Stock Ledger (Alerts tab) */}
          {activePanel === PANEL_ALERTS && !drillItem && (
            <div className="card shadow-sm mb-4" data-testid="inventory-overview">
              <div className="card-header bg-white py-3 border-bottom">
                <h5 className="card-title mb-0 font-weight-bold text-dark">
                  <i className="fa-solid fa-gauge mr-2 text-primary" />Live Stock Ledger
                </h5>
              </div>

              {/* ── Filter Bar ─────────────────────────────────────────── */}
              <div className="px-3 pt-3 pb-2 border-bottom" data-testid="ledger-filter-bar">
                <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                  {/* Search */}
                  <div className="input-group" style={{ maxWidth: 240, minWidth: 160 }}>
                    <div className="input-group-prepend">
                      <span className="input-group-text bg-white border-right-0">
                        <i className="fa-solid fa-magnifying-glass text-muted" style={{ fontSize: 12 }} />
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control border-left-0"
                      placeholder="Search ingredients…"
                      value={searchInput}
                      onChange={(e) => handleSearchInput(e.target.value)}
                      aria-label="Search ingredients"
                      data-testid="ledger-search"
                    />
                  </div>

                  {/* Category dropdown */}
                  <div className="dropdown" data-testid="ledger-category-dropdown">
                    <button
                      type="button"
                      className={`btn btn-outline-secondary btn-sm dropdown-toggle ${selectedCategories.length ? 'border-primary text-primary' : ''}`}
                      onClick={() => setCategoryOpen((o) => !o)}
                      aria-expanded={categoryOpen}
                      data-testid="ledger-category-btn"
                    >
                      Category
                      {selectedCategories.length > 0 && (
                        <span className="badge badge-primary ml-1" data-testid="ledger-category-count">{selectedCategories.length}</span>
                      )}
                    </button>
                    {categoryOpen && (
                      <div className="dropdown-menu show p-2" style={{ minWidth: 180, zIndex: 1050 }} data-testid="ledger-category-menu">
                        {allCategories.length === 0 && <small className="text-muted px-2">No categories</small>}
                        {allCategories.map((cat) => (
                          <label key={cat} className="dropdown-item d-flex align-items-center py-1" style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              className="mr-2"
                              checked={selectedCategories.includes(cat)}
                              onChange={() => toggleCategory(cat)}
                              data-testid={`ledger-cat-${cat}`}
                            />
                            {cat}
                          </label>
                        ))}
                        {selectedCategories.length > 0 && (
                          <button type="button" className="btn btn-link btn-sm text-danger p-0 mt-1 ml-2" onClick={() => updateParam('category', [])}>
                            Clear ×
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Low Stock toggle */}
                  <button
                    type="button"
                    className={`btn btn-sm ${lowStockActive ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={toggleLowStock}
                    aria-pressed={lowStockActive}
                    data-testid="ledger-low-stock-toggle"
                  >
                    <i className="fa-solid fa-triangle-exclamation mr-1" />
                    Low Stock {lowStockCount > 0 && <span className="badge badge-dark ml-1" data-testid="ledger-low-stock-count">{lowStockCount}</span>}
                  </button>

                  {/* Near Expiry toggle */}
                  <button
                    type="button"
                    className={`btn btn-sm ${nearExpiryActive ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={toggleNearExpiry}
                    aria-pressed={nearExpiryActive}
                    data-testid="ledger-near-expiry-toggle"
                  >
                    <i className="fa-solid fa-clock mr-1" />
                    Near Expiry {nearExpiryCount > 0 && <span className="badge badge-light ml-1" data-testid="ledger-near-expiry-count">{nearExpiryCount}</span>}
                  </button>

                  {/* Export CSV — right-aligned */}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ml-auto"
                    onClick={exportLedgerCSV}
                    data-testid="ledger-export-csv"
                  >
                    <i className="fa-solid fa-download mr-1" />Export CSV
                  </button>
                </div>

                {/* Row count */}
                <small className="text-muted d-block mt-2" data-testid="ledger-row-count">
                  {(searchQ || selectedCategories.length || lowStockActive || nearExpiryActive)
                    ? `Showing ${filteredOverview.length} of ${overview.length} ingredients`
                    : `Showing all ${overview.length} ingredients`}
                </small>
              </div>

              {/* ── Table ─────────────────────────────────────────────── */}
              <div className="card-body p-0" onClick={() => categoryOpen && setCategoryOpen(false)}>
                <div className="table-responsive">
                  <table className="table table-hover mb-0 text-center" data-testid="overview-table">
                    <thead>
                      <tr>
                        <th className="text-left">Ingredient</th>
                        <th className="text-left">Category</th>
                        <th>On Hand</th>
                        <th>Par Level</th>
                        <th>Status</th>
                        <th>Alerts</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Empty state */}
                      {filteredOverview.length === 0 && (
                        <tr data-testid="ledger-empty-state">
                          <td colSpan="7" className="py-5 text-center">
                            <i className="fa-solid fa-box-open fa-2x text-muted mb-3 d-block" />
                            <strong className="d-block text-dark mb-1">No ingredients match your filters</strong>
                            <small className="text-muted d-block mb-3">Try adjusting your search or clearing a filter</small>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearAllFilters} data-testid="ledger-clear-filters">
                              Clear all filters
                            </button>
                          </td>
                        </tr>
                      )}
                      {filteredOverview.map((it) => {
                        const status = rowStatus(it);
                        const statusBadge = status === 'Critical' ? 'badge-danger'
                          : status === 'Low Stock' || status === 'Near Expiry' ? 'badge-warning'
                          : 'badge-success';
                        return (
                          <tr key={it.id} data-testid={`overview-row-${it.id}`}>
                            <td className="text-left">
                              <strong>{it.name}</strong>
                              <br /><small className="text-muted">{it.item_code}</small>
                            </td>
                            <td className="text-left">
                              <small className="text-muted">{categoryMap[it.id] || '—'}</small>
                            </td>
                            <td>
                              <span className={it.has_negative ? 'text-danger font-weight-bold' : ''} data-testid={`overview-balance-${it.id}`}>
                                {Number(it.total_balance).toFixed(3)} {it.uom}
                              </span>
                              {it.has_negative && <span className="badge badge-danger ml-1" data-testid={`neg-badge-${it.id}`}>Neg</span>}
                            </td>
                            <td data-testid={`overview-threshold-${it.id}`}>
                              {it.threshold_configured
                                ? `${Number(it.safety_stock_threshold).toFixed(1)} ${it.safety_stock_unit || it.uom}`
                                : <small className="text-warning">Not set</small>}
                            </td>
                            <td>
                              <span className={`badge ${statusBadge}`} data-testid={`overview-status-${it.id}`}>{status}</span>
                            </td>
                            <td>
                              {(it.alert_conditions || []).length > 0
                                ? <small className="text-danger">{(it.alert_conditions || []).join(', ')}</small>
                                : <span className="text-muted">—</span>}
                            </td>
                            <td>
                              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openDrillDown(it)} data-testid={`btn-overview-drilldown-${it.id}`}>
                                Batches
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Batch Drill-down */}
          {activePanel === PANEL_ALERTS && drillItem && (
            <div className="card shadow-sm mb-4" data-testid="batch-drilldown">
              <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0 font-weight-bold text-dark">
                  <i className="fa-solid fa-layer-group mr-2 text-primary" />FEFO Batch Ledger — {drillItem.name}
                </h5>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setDrillItem(null)} data-testid="btn-close-drilldown">
                  Back to Overview
                </button>
              </div>
              <div className="card-body p-0">
                {batchLoading ? (
                  <div className="p-4 text-center text-muted">Loading batches...</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0" data-testid="batch-table">
                      <thead>
                        <tr>
                          <th>Batch ID</th>
                          <th>Lot No.</th>
                          <th>Qty</th>
                          <th>Expiry</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batches.map((b) => (
                          <tr key={b.id} data-testid={`batch-row-${b.id}`}
                            className={b.quarantined ? 'text-muted' : b.is_expired ? 'table-danger' : b.is_expiring_soon ? 'table-warning' : ''}>
                            <td><small className="font-weight-bold" data-testid={`batch-internal-id-${b.id}`}>{b.internal_batch_id || '—'}</small></td>
                            <td data-testid={`batch-lot-${b.id}`}>{b.batch_lot_number || '—'}</td>
                            <td data-testid={`batch-qty-${b.id}`}>{Number(b.quantity).toFixed(3)} {b.unit}</td>
                            <td data-testid={`batch-expiry-${b.id}`}>{b.expiry_date ? String(b.expiry_date).slice(0, 10) : '—'}</td>
                            <td>
                              {b.quarantined
                                ? <span className="badge badge-secondary" data-testid={`batch-status-quarantined-${b.id}`}>Quarantined</span>
                                : b.is_expired
                                ? <span className="badge badge-danger" data-testid={`batch-status-expired-${b.id}`}>Expired</span>
                                : b.is_expiring_soon
                                ? <span className="badge badge-warning" data-testid={`batch-status-expiring-${b.id}`}>Expiring Soon</span>
                                : <span className="badge badge-success">Active</span>}
                            </td>
                            <td>
                              {!b.quarantined && b.is_expired && (
                                <button type="button" className="btn btn-sm btn-danger" onClick={() => quarantineBatch(b.id)} data-testid={`btn-quarantine-${b.id}`}>
                                  Quarantine/Dispose
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {batches.length === 0 && (
                          <tr><td colSpan="6" className="text-muted py-3 text-center" data-testid="batch-empty">No batches found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stock Receipts Ledger (receipt/adjust tabs) */}
          {activePanel !== PANEL_ALERTS && activePanel !== PANEL_REORDER && (
            <>
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-white py-3 border-bottom">
                  <h5 className="card-title mb-0 font-weight-bold text-dark">
                    <i className="fa-solid fa-table-list mr-2 text-primary" />Live Stock Ledger (Receipts)
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 text-center" data-testid="ledger-table">
                      <thead>
                        <tr>
                          <th className="text-left">Ingredient</th>
                          <th>Batch ID</th>
                          <th>Lot Number</th>
                          <th>Qty Received</th>
                          <th>Expiry Date</th>
                          <th>Vendor</th>
                          <th>Cost/Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredReceipts.length === 0 && <tr><td colSpan="7" className="text-muted py-4" data-testid="ledger-empty">No stock receipts found.</td></tr>}
                        {filteredReceipts.map((r) => (
                          <tr key={r.id} data-testid={`ledger-row-${r.id}`}>
                            <td className="text-left"><strong data-testid={`ledger-item-${r.id}`}>{r.item_name}</strong><br /><small className="text-muted">{r.item_code}</small></td>
                            <td><small className="text-muted font-weight-bold" data-testid={`ledger-batch-id-${r.id}`}>{r.internal_batch_id || '—'}</small></td>
                            <td data-testid={`ledger-lot-${r.id}`}>{r.batch_lot_number || '—'}</td>
                            <td><strong data-testid={`ledger-qty-${r.id}`}>{Number(r.quantity).toFixed(3)}</strong>{' '}<small className="text-muted">{r.unit || r.item_uom}</small></td>
                            <td data-testid={`ledger-expiry-${r.id}`}>{r.expiry_date ? String(r.expiry_date).slice(0, 10) : '—'}</td>
                            <td data-testid={`ledger-vendor-${r.id}`}>{r.vendor_name || '—'}</td>
                            <td data-testid={`ledger-cost-${r.id}`}>{r.purchase_cost != null ? `$${Number(r.purchase_cost).toFixed(2)}` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="card shadow-sm">
                <div className="card-header bg-white py-3 border-bottom">
                  <h5 className="card-title mb-0 font-weight-bold text-dark">
                    <i className="fa-solid fa-clock-rotate-left mr-2 text-warning" />Manual Adjustment History
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 text-center" data-testid="adj-table">
                      <thead>
                        <tr>
                          <th className="text-left">Ingredient</th>
                          <th>Adj Qty</th>
                          <th>Before</th>
                          <th>After</th>
                          <th>Reason</th>
                          <th>Negative</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAdjustments.length === 0 && <tr><td colSpan="7" className="text-muted py-3" data-testid="adj-empty">No adjustments recorded.</td></tr>}
                        {filteredAdjustments.map((a) => (
                          <tr key={a.id} data-testid={`adj-row-${a.id}`}>
                            <td className="text-left"><strong data-testid={`adj-item-${a.id}`}>{a.item_name}</strong><br /><small className="text-muted">{a.item_code}</small></td>
                            <td><span className={Number(a.adjustment_qty) >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'} data-testid={`adj-qty-${a.id}`}>{Number(a.adjustment_qty) >= 0 ? '+' : ''}{Number(a.adjustment_qty).toFixed(3)}</span>{' '}<small className="text-muted">{a.unit || a.item_uom}</small></td>
                            <td data-testid={`adj-before-${a.id}`}>{Number(a.balance_before).toFixed(3)}</td>
                            <td data-testid={`adj-after-${a.id}`}>{Number(a.balance_after).toFixed(3)}</td>
                            <td className="text-left" style={{ maxWidth: 160 }} data-testid={`adj-reason-${a.id}`}><small>{a.reason}</small></td>
                            <td>{a.went_negative ? <span className="badge badge-danger" data-testid={`adj-neg-badge-${a.id}`}>Negative</span> : <span className="text-muted">—</span>}</td>
                            <td><small className="text-muted" data-testid={`adj-date-${a.id}`}>{new Date(a.created_at).toLocaleDateString()}</small></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Reorder Request List (Reorder tab) */}
          {activePanel === PANEL_REORDER && (
            <div className="card shadow-sm" data-testid="reorder-list-panel">
              <div className="card-header bg-white py-3 border-bottom">
                <h5 className="card-title mb-0 font-weight-bold text-dark">
                  <i className="fa-solid fa-list-check mr-2 text-primary" />Reorder Requests
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 text-center" data-testid="reorder-table">
                    <thead>
                      <tr>
                        <th className="text-left">Ref</th>
                        <th className="text-left">Ingredient</th>
                        <th>Requested Qty</th>
                        <th>Current Balance</th>
                        <th>Urgency</th>
                        <th>Required By</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reorderList.length === 0 && (
                        <tr><td colSpan="7" className="text-muted py-4" data-testid="reorder-empty">No reorder requests yet.</td></tr>
                      )}
                      {reorderList.map((rr) => (
                        <tr key={rr.id} data-testid={`reorder-row-${rr.id}`}>
                          <td className="text-left"><small className="font-weight-bold text-primary" data-testid={`reorder-ref-${rr.id}`}>{rr.requisition_ref}</small></td>
                          <td className="text-left"><strong data-testid={`reorder-item-name-${rr.id}`}>{rr.item_name}</strong><br /><small className="text-muted">{rr.item_code}</small></td>
                          <td data-testid={`reorder-qty-${rr.id}`}>{Number(rr.requested_qty).toFixed(3)} {rr.unit}</td>
                          <td data-testid={`reorder-balance-${rr.id}`}>{Number(rr.current_balance).toFixed(3)} {rr.unit}</td>
                          <td>
                            <span className={`badge ${rr.urgency === 'High' ? 'badge-danger' : rr.urgency === 'Medium' ? 'badge-warning' : 'badge-secondary'}`} data-testid={`reorder-urgency-${rr.id}`}>
                              {rr.urgency}
                            </span>
                          </td>
                          <td data-testid={`reorder-date-${rr.id}`}>{rr.required_by_date ? String(rr.required_by_date).slice(0, 10) : '—'}</td>
                          <td><span className="badge badge-info" data-testid={`reorder-status-${rr.id}`}>{rr.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
